import { Link, NavLink, Outlet } from 'react-router-dom';
import { LayoutDashboard, Truck, Image, History, LogOut, ExternalLink, GraduationCap } from 'lucide-react';
import { Logo } from './Logo';
import { Button } from '@ui/Button';
import { useSession } from '@shared/hooks/useSession';
import { ToastProvider } from '@ui/Toast';
import { TooltipProvider } from '@ui/Tooltip';
import { api } from '@shared/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { qk } from '@shared/lib/query-keys';
import { useNavigate } from 'react-router-dom';
import { cn } from '@shared/lib/cn';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/fahrzeuge', label: 'Fahrzeuge', icon: Truck },
  { to: '/admin/lernen', label: 'Lernen', icon: GraduationCap },
  { to: '/admin/medien', label: 'Medien', icon: Image },
  { to: '/admin/audit', label: 'Audit-Log', icon: History },
];

export function AdminShell() {
  const { data: user } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const onLogout = async () => {
    await api('/api/admin/logout', { method: 'POST' });
    queryClient.setQueryData(qk.session, null);
    navigate('/admin/login', { replace: true });
  };

  return (
    <TooltipProvider>
      <ToastProvider>
        <div className="flex min-h-screen bg-bg-0">
          {/* Sidebar */}
          <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-line-subtle/10 bg-bg-1/60 backdrop-blur-md md:flex">
            <div className="flex h-16 items-center gap-2.5 border-b border-line-subtle/10 px-5">
              <Logo className="h-7 w-7 text-accent-500" />
              <div className="leading-tight">
                <div className="text-sm font-semibold text-fg-hi">Backoffice</div>
                <div className="text-2xs uppercase tracking-[0.18em] text-fg-lo">FFW Arberg</div>
              </div>
            </div>

            <nav className="flex-1 space-y-1 p-3">
              {NAV.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive ? 'bg-bg-2 text-fg-hi' : 'text-fg-md hover:bg-bg-2 hover:text-fg-hi'
                    )
                  }
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="space-y-2 border-t border-line-subtle/10 p-3">
              <Link
                to="/"
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-xs text-fg-lo hover:bg-bg-2 hover:text-fg-md"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Öffentliche Seite
              </Link>
              <div className="flex items-center justify-between rounded-lg bg-bg-2 px-3 py-2.5">
                <div className="leading-tight">
                  <div className="text-xs font-medium text-fg-hi">{user?.username}</div>
                  <div className="text-2xs uppercase tracking-wider text-fg-lo">{user?.role}</div>
                </div>
                <button
                  onClick={onLogout}
                  className="rounded-md p-1.5 text-fg-lo hover:bg-bg-3 hover:text-danger"
                  title="Abmelden"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </aside>

          {/* Main */}
          <div className="flex min-w-0 flex-1 flex-col">
            <header className="sticky top-0 z-40 border-b border-line-subtle/10 bg-bg-0/80 backdrop-blur-md">
              <div className="flex h-16 items-center justify-between px-4 md:px-8">
                <div className="text-sm font-medium text-fg-md">
                  Backoffice
                </div>
                <Button variant="ghost" size="sm" onClick={onLogout} className="md:hidden">
                  <LogOut className="h-4 w-4" />
                  Abmelden
                </Button>
              </div>
              {/* Mobile-Navigation: Sidebar ist <md ausgeblendet */}
              <nav className="flex gap-1 overflow-x-auto px-3 pb-2 md:hidden">
                {NAV.map(item => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      cn(
                        'flex min-h-[40px] shrink-0 items-center gap-1.5 rounded-lg px-3 text-sm font-medium transition-colors',
                        isActive ? 'bg-bg-2 text-fg-hi' : 'text-fg-md hover:bg-bg-2 hover:text-fg-hi'
                      )
                    }
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </header>
            <main className="flex-1 overflow-x-hidden p-4 md:p-8">
              <Outlet />
            </main>
          </div>
        </div>
      </ToastProvider>
    </TooltipProvider>
  );
}
